import { useMemo } from 'react';
import { usePersistentState } from '@hooks/usePersistentState';
import { Highlighted, OriginBadge } from './cxFormat';

const INPUT_LABELS = { digits: 'entry', timeout: 'no key', anyKey: 'any key' };

function Switch({ on, disabled, title, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className={`cx-sw${on ? ' cx-sw-on' : ''}`}
      disabled={disabled}
      title={title}
      onClick={(e) => { e.stopPropagation(); onChange(!on); }}
    />
  );
}

/** One spoken line: label, where its words come from, settings and the text. */
function PromptLine({ prompt, ctx, lang, onOpen, onToggle, togglePending }) {
  const silent = !prompt.text?.trim();
  const toggleTitle = ctx.isCompany
    ? 'Turn this question on or off for this company'
    : ctx.isParent ? 'Questions can only be turned off per company, not for a parent group'
      : 'On by default. View a company to turn it off.';
  return (
    <div
      className={`cx-say${prompt.enabled ? '' : ' cx-dim'}${prompt.coachOnly ? ' cx-say-coach' : ''}`}
      onClick={() => onOpen(prompt)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(prompt); }}
      role="button"
      tabIndex={0}
    >
      <span className="cx-say-ico" aria-hidden="true">{prompt.coachOnly ? '🎧' : '🔊'}</span>
      <div className="cx-say-main">
        <div className="cx-say-meta">
          <span className="cx-say-label">{prompt.label}</span>
          {prompt.when && <span className="cx-when" title="Spoken only when">{prompt.when}</span>}
          <OriginBadge origin={prompt.origin} />
          {prompt.required && <span className="cx-badge cx-badge-req" title="Can't be turned off">required</span>}
          {prompt.toggleable && (
            <Switch on={prompt.enabled} disabled={!ctx.isCompany || togglePending} title={toggleTitle}
              onChange={(enabled) => onToggle(prompt.key, enabled)} />
          )}
          {prompt.customizedCount > 0 && <span className="cx-badge cx-badge-co">custom for {prompt.customizedCount}</span>}
          {prompt.disabledCount > 0 && <span className="cx-badge cx-badge-off">off for {prompt.disabledCount}</span>}
          {!prompt.enabled && <span className="cx-badge cx-badge-off">off for {ctx.name}</span>}
          {lang === 'es' && prompt.spanishMissing && !silent && (
            <span className="cx-badge cx-badge-warn" title="Spanish callers hear the English wording">no Spanish</span>
          )}
          {prompt.coachOnly && <span className="cx-when">coach hears</span>}
        </div>
        {silent
          ? <div className="cx-say-text cx-silent">Silent: {prompt.note || 'no wording set'}</div>
          : <div className="cx-say-text"><Highlighted text={prompt.text} /></div>}
      </div>
    </div>
  );
}

function Branch({ branch, stepTitles }) {
  const impossible = branch.guardValue === false;
  const input = branch.input === 'digit'
    ? <span className="cx-key">{branch.value}</span>
    : INPUT_LABELS[branch.input] ? <span className="cx-key cx-key-wide">{INPUT_LABELS[branch.input]}</span> : null;
  return (
    <div className={`cx-br${impossible ? ' cx-br-dim' : ''}`}>
      {input}
      <span className="cx-br-label">{branch.label}</span>
      <span className="cx-br-target">→ {stepTitles[branch.target] || branch.target}</span>
      {branch.guard && !impossible && <span className="cx-when" title="Condition">{branch.guard}</span>}
      {impossible && <span className="cx-br-why">Doesn&apos;t apply: {branch.guardReason}</span>}
    </div>
  );
}

/** Prompts in declared order, with expanded question groups under a subheading. */
function PromptList({ prompts, ...props }) {
  const blocks = [];
  prompts.forEach((p) => {
    const last = blocks[blocks.length - 1];
    if (p.group && last?.group === p.group) last.items.push(p);
    else blocks.push({ group: p.group, label: p.groupLabel, items: [p] });
  });
  return blocks.map((b, i) => (b.group ? (
    <div key={`${b.group}-${i}`} className="cx-sub">
      <div className="cx-subhead">{b.label}</div>
      {b.items.map((p) => <PromptLine key={`${p.kind}:${p.key}`} prompt={p} {...props} />)}
    </div>
  ) : b.items.map((p) => <PromptLine key={`${p.kind}:${p.key}:${p.when || ''}`} prompt={p} {...props} />)));
}

function Step({ step, num, collapsed, onCollapse, stepTitles, ctx, onToggle, togglePending, ...props }) {
  const off = !step.enabled;
  return (
    <div className={`cx-step${off ? ' cx-step-off' : ''}${collapsed ? ' cx-step-collapsed' : ''}`}>
      <div
        className="cx-shead"
        onClick={onCollapse}
        onKeyDown={(e) => { if (e.key === 'Enter') onCollapse(); }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
      >
        <span className={`cx-num${step.outcome ? ' cx-num-end' : ''}`}>{num}</span>
        <span className="cx-stitle">{step.title}</span>
        {step.toggle && (
          <>
            <Switch
              on={step.enabled}
              disabled={!ctx.isCompany || togglePending}
              title={ctx.isCompany ? 'Turn this step on or off for this company' : 'View a company to turn this step off'}
              onChange={(enabled) => onToggle(step.toggle, enabled)}
            />
            <code className="cx-muted">{step.toggle}</code>
          </>
        )}
        {off && <span className="cx-offnote">Skipped for {ctx.name}: {step.offNote}</span>}
        {!off && step.disabledCount > 0 && <span className="cx-badge cx-badge-off">off for {step.disabledCount}</span>}
        <span className="cx-caret" aria-hidden="true">▾</span>
      </div>
      {!collapsed && (
        <div className="cx-sbody">
          {step.note && <div className="cx-note">{step.note}</div>}
          {(step.skips || []).length > 0 && (
            <div className="cx-skips">
              <div className="cx-subhead">Not asked when</div>
              {step.skips.map((b, i) => <Branch key={i} branch={b} stepTitles={stepTitles} />)}
            </div>
          )}
          <PromptList prompts={step.prompts} ctx={ctx} onToggle={onToggle} togglePending={togglePending} {...props} />
          {step.gather && (
            <div className="cx-note">
              Waits for {step.gather.numDigits === 1 ? 'a key' : `up to ${step.gather.numDigits} digits`}
              {step.gather.finishOnHash ? ' ended with #' : ''} (timeout:{' '}
              {step.gather.timeoutSeconds != null ? `${step.gather.timeoutSeconds} s` : <code>IvrOptions.{step.gather.timeoutOption}</code>}).
              {step.retry && ` Re-asked up to ${step.retry.maxAttemptsOption} times, then → ${stepTitles[step.retry.giveUpTarget] || step.retry.giveUpTarget}.`}
            </div>
          )}
          {step.repeat && <div className="cx-note">Asked once per item ({step.repeat.itemInputs.join(', ')}).</div>}
          {step.branches.length > 0 && (
            <div className="cx-branches">
              {step.branches.map((b, i) => <Branch key={i} branch={b} stepTitles={stepTitles} />)}
            </div>
          )}
          {step.outcome && <div className="cx-end">{step.outcome}</div>}
        </div>
      )}
    </div>
  );
}

/** A journey as numbered steps, with error and fallback paths collected at the end. */
export default function JourneyView({ journey, ...props }) {
  const [collapsed, setCollapsed] = usePersistentState(`cx.collapsed.${journey.id}:${journey.productLineId ?? ''}`, {});
  const stepTitles = useMemo(() => Object.fromEntries(journey.steps.map((s) => [s.id, s.title])), [journey]);
  const main = journey.steps.filter((s) => s.group !== 'system');
  const system = journey.steps.filter((s) => s.group === 'system');

  const setAll = (value) => setCollapsed(Object.fromEntries(journey.steps.map((s) => [s.id, value])));
  const flip = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  return (
    <div className="cx-journey">
      <div className="cx-journey-tools">
        <button type="button" className="cx-btn cx-btn-small" onClick={() => setAll(false)}>Expand all</button>
        <button type="button" className="cx-btn cx-btn-small" onClick={() => setAll(true)}>Collapse all</button>
      </div>
      {main.map((step, i) => (
        <Step key={step.id} step={step} num={i + 1} collapsed={!!collapsed[step.id]} onCollapse={() => flip(step.id)}
          stepTitles={stepTitles} {...props} />
      ))}
      {system.length > 0 && (
        <>
          <h3 className="cx-section-title">Error and fallback paths</h3>
          {system.map((step) => (
            <Step key={step.id} step={step} num="!" collapsed={collapsed[step.id] ?? true} onCollapse={() => setCollapsed((c) => ({ ...c, [step.id]: !(c[step.id] ?? true) }))}
              stepTitles={stepTitles} {...props} />
          ))}
        </>
      )}
    </div>
  );
}
